import {
  COUNTRY_SELECT_OPTIONS,
  INDUSTRY_SELECT_OPTIONS,
  STATE_SELECT_OPTIONS
} from 'constants/firmographics'
import moment from 'moment'

import {
  fromTimestamp,
  getDefaultDate,
  getDefaultRange,
  toTimestamp
} from 'components/DateRangePicker'
import { ActivityType, activityTypeToString, ContactType } from 'modules/accountActivity/domain'

export enum FilterValueType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN'
}

export enum FilterPlurality {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE'
}

export enum FilterOperator {
  EQUALS = 'EQUALS',
  CONTAINS = 'CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  DOES_NOT_EQUAL = 'DOES_NOT_EQUAL',
  DOES_NOT_CONTAIN = 'DOES_NOT_CONTAIN',
  DOES_NOT_START_WITH = 'DOES_NOT_START_WITH',
  BETWEEN = 'BETWEEN',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQ = 'GREATER_THAN_EQ',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQ = 'LESS_THAN_EQ'
}

export enum AccountType {
  ALL = 'ALL',
  OWNED = 'OWNED',
  TARGETED = 'TARGETED'
}

export enum FilterableEntity {
  ACTIVITIES = 'ACTIVITIES',
  CONTACT_TYPES = 'CONTACT_TYPES',
  ACCOUNT_OWNERS = 'ACCOUNT_OWNERS',
  DATE_RANGE = 'DATE_RANGE',
  PAGE_URL = 'PAGE_URL',
  IN_CRM = 'IN_CRM',
  TARGETED = 'TARGETED',
  CONTACT_ROLE = 'CONTACT_ROLE',
  NUMBER = 'NUMBER',
  CUSTOM_ATTRIBUTE = 'CUSTOM_ATTRIBUTE',
  FIRMOGRAPHICS = 'FIRMOGRAPHICS',
  FIRMOGRAPHICS_INDUSTRY = 'firmographic_Industry',
  FIRMOGRAPHICS_COUNTRY = 'firmographic_Country',
  FIRMOGRAPHICS_STATE = 'firmographic_State',
  FIRMOGRAPHICS_POSTAL_CODE = 'firmographic_PostalCode',
  FIRMOGRAPHICS_EMPLOYEE_COUNT = 'firmographic_EmployeeCount',
  LAST_TOUCHED = 'lastTouched'
}

export interface FilterValues {
  filterType: FilterableEntity
  filterSubtype?: string
  attributeType?: string
  valueType?: string
  operator: FilterOperator
  values: any
}

export interface ContextFilter {
  field: string
  filterOperation: string
  attribute: string
  values: string[]
}

export interface ContextFilters {
  [key: string]: ContextFilter[]
}

export interface AttributeFilter {
  filterOperation: string
  attribute?: string
  values: string[]
}

export const ALL_ACTIVITY_TYPES = new Set([
  ActivityType.PAGE_VIEW,
  ActivityType.CONVERSATION,
  ActivityType.MEETING_BOOKED,
  ActivityType.VIDEO,
  ActivityType.EMAIL_OPEN,
  ActivityType.EMAIL_CLICK,
  ActivityType.EMAIL_INBOUND,
  ActivityType.WEBINAR,
  ActivityType.SMS_INBOUND,
  ActivityType.CONTRACT_REQUEST,
  ActivityType.FILLED_OUT_FORM,
  ActivityType.UNSUBSCRIBED
])

export const NUMBER_OPERATOR_TYPES = new Set([
  FilterOperator.LESS_THAN_EQ,
  FilterOperator.LESS_THAN,
  FilterOperator.GREATER_THAN,
  FilterOperator.GREATER_THAN_EQ
])

export const ALL_CONTACT_TYPES = new Set([ContactType.KNOWN, ContactType.UNKNOWN])

export const operatorToString = (operator: FilterOperator): string => {
  switch (operator) {
    case FilterOperator.EQUALS:
      return 'is'
    case FilterOperator.CONTAINS:
      return 'contains'
    case FilterOperator.STARTS_WITH:
      return 'starts with'
    case FilterOperator.DOES_NOT_EQUAL:
      return 'is not'
    case FilterOperator.DOES_NOT_CONTAIN:
      return 'does not contain'
    case FilterOperator.DOES_NOT_START_WITH:
      return 'does not start with'
    case FilterOperator.BETWEEN:
      return 'between'
    case FilterOperator.GREATER_THAN:
      return '>'
    case FilterOperator.GREATER_THAN_EQ:
      return '≥'
    case FilterOperator.LESS_THAN:
      return '<'
    case FilterOperator.LESS_THAN_EQ:
      return '≤'
  }
}

export interface FilterConfiguration {
  filterType: FilterableEntity
  filterSubtype?: string
  filterDisplayName: string
  plurality: FilterPlurality
  operators: FilterOperator[]
  valueType: string
  extraWord: string
  creatable?: boolean
  gridTemplateStyle: { gridTemplateColumns: string }
  getIsFilterValid: (value: any) => boolean
  transformFilterForApi: (filter: any) => any
  attributeType?: string
}

export interface FilterConfigAndAvailableValues extends FilterConfiguration {
  values: any[]
}
const firmographicsFilterOptions = {
  extraWord: 'more',
  creatable: false,
  getIsFilterValid: (value: any) => {
    return (Array.isArray(value) && value.length > 0) || (Number.isFinite(value) && value > 0)
  },
  transformFilterForApi: (filter: any) => {
    return Array.isArray(filter?.value) && filter.value.length
      ? {
        field: `act.${filter.filterSubType}`,
        filterOperation: filter.operator,
        values: filter.values.map((value: any) => value.value)
      }
      : undefined
  },
  gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
}

export const filterConfig: FilterConfigAndAvailableValues[] = [
  {
    filterType: FilterableEntity.ACCOUNT_OWNERS,
    filterDisplayName: 'Account owner',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'others',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
          field: 'acct.accountOwnerId',
          filterOperation: filter.operator,
          values: filter.values.map((value: any) => value.value)
        }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.ACTIVITIES,
    filterDisplayName: 'Activity type',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [...ALL_ACTIVITY_TYPES].map(activity => ({
      value: activity,
      label: activityTypeToString(activity)
    })),
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (value: any) => {
      if (Array.isArray(value) && value.length > 0) {
        const result = value?.flatMap((item: any) => {
          if (item.value === ActivityType.EMAIL_CLICK) {
            return [ActivityType.EMAIL_CLICK, ActivityType.EMAIL_CLICK_SFDC]
          } else if (item.value === ActivityType.EMAIL_OPEN) {
            return [ActivityType.EMAIL_OPEN, ActivityType.EMAIL_OPEN_SFDC]
          } else {
            return item.value
          }
        });
        return result;
      }
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.CONTACT_TYPES,
    filterDisplayName: 'Contact is known',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [
      { label: 'true', value: ContactType.KNOWN },
      { label: 'false', value: ContactType.UNKNOWN }
    ],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Object.values(ContactType).includes(value?.value)
    },
    transformFilterForApi: (value: any) => {
      return value?.value || ContactType.ALL
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.TARGETED,
    filterDisplayName: 'Account type',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [
      { label: 'Targeted', value: AccountType.TARGETED },
      { label: 'All', value: AccountType.ALL }
    ],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Object.values(AccountType).includes(value?.value)
    },
    transformFilterForApi: (value: any) => {
      return value?.value === AccountType.TARGETED ? true : false
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.IN_CRM,
    filterDisplayName: 'In CRM',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [
      { label: 'true', value: true },
      { label: 'false', value: false }
    ],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: () => {
      return true
    },
    transformFilterForApi: (value: any) => {
      return value
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.PAGE_URL,
    filterDisplayName: 'Page visited',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.CONTAINS, FilterOperator.DOES_NOT_CONTAIN],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'more',
    creatable: true,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
          PAGE_VIEW: [
            {
              field: 'act.activityContext',
              filterOperation: filter.operator,
              attribute: 'pageUrl',
              values: filter.values.map((value: any) => value.value)
            }
          ]
        }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.CONTACT_ROLE,
    filterDisplayName: 'Contact role',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.CONTAINS, FilterOperator.DOES_NOT_CONTAIN],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'more',
    creatable: true,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
          field: 'act.endUserData',
          filterOperation: filter.operator,
          attribute: 'role',
          values: filter.values.map((value: any) => value.value)
        }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.NUMBER,
    filterDisplayName: 'Number',
    plurality: FilterPlurality.SINGLE,
    operators: [
      FilterOperator.EQUALS,
      FilterOperator.DOES_NOT_EQUAL,
      FilterOperator.LESS_THAN,
      FilterOperator.LESS_THAN_EQ,
      FilterOperator.GREATER_THAN,
      FilterOperator.GREATER_THAN_EQ
    ],
    valueType: FilterValueType.NUMBER,
    values: [],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return typeof value === 'number'
    },
    transformFilterForApi: (filter: FilterValues) => {
      return typeof filter.values === 'number' ? [filter.values] : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.FIRMOGRAPHICS,
    filterSubtype: FilterableEntity.FIRMOGRAPHICS_INDUSTRY,
    filterDisplayName: 'Industry',
    plurality: FilterPlurality.MULTIPLE,
    operators: [
      FilterOperator.EQUALS,
      FilterOperator.DOES_NOT_EQUAL,
      FilterOperator.CONTAINS,
      FilterOperator.DOES_NOT_CONTAIN
    ],
    valueType: FilterValueType.STRING,
    values: INDUSTRY_SELECT_OPTIONS,
    ...firmographicsFilterOptions,
    creatable: true
  },
  {
    filterType: FilterableEntity.FIRMOGRAPHICS,
    filterSubtype: FilterableEntity.FIRMOGRAPHICS_STATE,
    filterDisplayName: 'State',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
    valueType: FilterValueType.STRING,
    values: STATE_SELECT_OPTIONS(),
    ...firmographicsFilterOptions
  },
  {
    filterType: FilterableEntity.FIRMOGRAPHICS,
    filterSubtype: FilterableEntity.FIRMOGRAPHICS_COUNTRY,
    filterDisplayName: 'Country',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
    valueType: FilterValueType.STRING,
    values: COUNTRY_SELECT_OPTIONS,
    ...firmographicsFilterOptions
  },
  {
    filterType: FilterableEntity.FIRMOGRAPHICS,
    filterSubtype: FilterableEntity.FIRMOGRAPHICS_POSTAL_CODE,
    filterDisplayName: 'Postal code',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
    valueType: FilterValueType.STRING,
    values: [],
    ...firmographicsFilterOptions,
    creatable: true
  },
  {
    filterType: FilterableEntity.FIRMOGRAPHICS,
    filterSubtype: FilterableEntity.FIRMOGRAPHICS_EMPLOYEE_COUNT,
    filterDisplayName: 'Company size',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.GREATER_THAN_EQ, FilterOperator.LESS_THAN_EQ],
    valueType: FilterValueType.NUMBER,
    values: [],
    ...firmographicsFilterOptions,
    creatable: true
  },
  {
    filterType: FilterableEntity.DATE_RANGE,
    filterDisplayName: 'Date range',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.DATE,
    values: [],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return !!value
    },
    transformFilterForApi: (filter: any) => {
      //TODO will not work until we have backend support for between on date range
      if (Array.isArray(filter.values)) {
        return filter.values.map((value: any) => moment(value.value).format('YYYY-MM-DD'))
      } else {
        return [moment(filter.values).endOf('day')]
      }
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.LAST_TOUCHED,
    filterDisplayName: 'Last touch',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.BETWEEN],
    valueType: FilterValueType.DATE,
    values: [],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return !!value
    },
    transformFilterForApi: (filter: any) => {
      if (filter?.values) {
        return {
          field: 'acct.lastTouchedAt',
          filterOperation: filter.operator,
          values: [
            moment(filter.values.startDate).valueOf(),
            moment(filter.values.endDate).valueOf()
          ]
        }
      }

      return undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  }
]

export const filterMap = filterConfig.reduce(
  (acc: { [key: string]: FilterConfiguration }, filterConfig: FilterConfiguration) => {
    acc[filterConfig.filterType] = filterConfig
    return acc
  },
  {}
)

export interface AttributeOption {
  label: string
  type: string
  options?: string[]
}

export const attributeOptionToFilter = (
  attribute: AttributeOption
): FilterConfigAndAvailableValues => {
  const filterSubtype = attribute.label
  const filterDisplayName = attribute.label
  const values = attribute.options
    ? attribute.options.map((option: any) => ({ label: option, value: option }))
    : []
  const attributeType = attribute.type
  const creatable =
    (!attribute.options || attribute.options.length === 0) && attributeType !== 'TEAMMEMBER'

  const templateFilter =
    attributeType === 'DATE' || attributeType === 'DATETIME'
      ? filterMap[FilterableEntity.DATE_RANGE]
      : attributeType === 'NUMBER'
        ? filterMap[FilterableEntity.NUMBER]
        : {
          plurality: FilterPlurality.MULTIPLE,
          operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
          valueType: FilterValueType.STRING,
          extraWord: 'more',
          getIsFilterValid: (value: any) => {
            return Array.isArray(value) && value.length > 0
          },
          transformFilterForApi: (x: any) => x,
          gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
        }

  return {
    ...templateFilter,
    filterType: FilterableEntity.CUSTOM_ATTRIBUTE,
    filterSubtype,
    filterDisplayName,
    values,
    creatable,
    attributeType
  }
}

export const getContactTypes = (contactTypes: Set<ContactType>) => {
  if (contactTypes.size === 0) return ContactType.ALL
  if (contactTypes.size === 2) return ContactType.ALL
  if (contactTypes.has(ContactType.KNOWN)) return ContactType.KNOWN
  return ContactType.UNKNOWN
}

export type FilterGroup = FilterValues[]

export const MAX_NUM_FILTER_GROUPS = 6

export const initialGroupsByPage = {
  ACCOUNTS: [[]],
  ACTIVITIES: [[]],
  ACCOUNT_DETAILS: [[]],
  CONTACT_DETAILS: [[]]
}

export enum FilterPage {
  ACCOUNTS = 'ACCOUNTS',
  ACTIVITIES = 'ACTIVITIES'
}

export interface FilterGroupsByPage {
  ACCOUNTS: FilterGroup[]
  ACTIVITIES: FilterGroup[]
}

const isKnownType = (filter: FilterValues) => {
  return filter.filterType in FilterableEntity
}

export const defaultFilterTypes = [
  FilterableEntity.ACCOUNT_OWNERS,
  FilterableEntity.TARGETED,
  FilterableEntity.IN_CRM,
  FilterableEntity.ACTIVITIES,
  FilterableEntity.CONTACT_TYPES,
  FilterableEntity.CONTACT_ROLE,
  FilterableEntity.PAGE_URL,
  FilterableEntity.CUSTOM_ATTRIBUTE,
  FilterableEntity.FIRMOGRAPHICS,
  FilterableEntity.LAST_TOUCHED
]

export const deserializeDateRange = (dateRangeJson: string) => {
  const { startDate, endDate, type } = JSON.parse(dateRangeJson)
  return { startDate: moment(startDate), endDate: moment(endDate), type }
}

export const normalize = (filter: FilterValues) => {
  const { filterType, filterSubtype, operator, attributeType } = filter
  let normValues = filter.values

  if (filterType === 'DATE_RANGE' || attributeType === 'DATE' || attributeType === 'DATETIME') {
    const { values } = filter

    if (moment.isMoment(values)) {
      normValues = values.valueOf()
    } else {
      normValues = toTimestamp(values)
    }
  }

  if (Array.isArray(normValues)) {
    normValues = normValues.map(({ label, value }) => ({
      label,
      value
    }))
  }

  return {
    filterType,
    ...(filterSubtype ? { filterSubtype } : {}),
    ...(attributeType ? { attributeType } : {}),
    operator,
    values: normValues
  }
}

export const serializeFilter = (filterGroups: FilterGroup[]): any => {
  const normalizedGroups = filterGroups.map((filters: FilterValues[]) =>
    filters.filter(isKnownType).map(normalize)
  )

  return normalizedGroups
}

export const serializeFilters = (filterGroupsByPage: FilterGroupsByPage): string => {
  const normalized = {} as any

  for (const [page, filterGroups] of Object.entries(filterGroupsByPage)) {
    normalized[page] = filterGroups.map((filters: FilterValues[]) =>
      filters.filter(isKnownType).map(normalize)
    )
  }

  return JSON.stringify({
    version: 2,
    filterGroupsByPage: normalized
  })
}

export const deserializeFilters = (json: string): FilterGroupsByPage | undefined => {
  const reviver = (key: string | symbol, value: any) => {
    // WORKAROUND: Replace the saved date with the default range (the last two
    // weeks). Users expect to see their most recent activity by default so
    // saving a fixed range was causing confusion.
    if (value?.filterType === FilterableEntity.DATE_RANGE) {
      return {
        ...value,
        operator: FilterOperator.BETWEEN,
        values: getDefaultRange()
      }
    }

    if (value?.attributeType === 'DATE' || value?.attributeType === 'DATETIME') {
      let normValues = value.values

      if (typeof value.values === 'object') {
        normValues = fromTimestamp(value.values)
      } else if (typeof value.values === 'number') {
        normValues = moment(value.values)
      }

      return {
        ...value,
        values: normValues
      }
    }

    // WORKAROUND: This label/value pair was mismatched. Repair the filter if
    // it was saved that way.
    if (
      value?.filterType === FilterableEntity.TARGETED &&
      value?.values?.label === 'Targeted' &&
      value?.values?.value === 'ALL'
    ) {
      return {
        ...value,
        values: { label: 'Targeted', value: 'TARGETED' }
      }
    }

    return value
  }

  const parsed = JSON.parse(json, reviver)

  if (parsed.version === 2) {
    return removeDeprecatedFilters(parsed.filterGroupsByPage)
  }

  if (parsed.version === 1) {
    return convertV1ToV2(parsed)
  }

  window.console.warn(`Filters version ${parsed.version} not supported.`)

  return undefined
}

const removeDeprecatedFilters = (filterGroupsByPage: FilterGroupsByPage): FilterGroupsByPage => {
  const filterDateRangesFromGroup = (group: FilterValues[]) => {
    return group.filter(
      ({ filterType }: FilterValues) => filterType !== FilterableEntity.DATE_RANGE
    )
  }

  filterGroupsByPage.ACTIVITIES = filterGroupsByPage.ACTIVITIES.map(filterDateRangesFromGroup)

  return filterGroupsByPage
}

function convertV1ToV2(parsed: { filterGroups: FilterValues[][] }) {
  const firstGroup = parsed.filterGroups[0]

  const supportedFilters = {
    ACCOUNTS: [
      FilterableEntity.ACCOUNT_OWNERS,
      FilterableEntity.TARGETED,
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL,
      FilterableEntity.CUSTOM_ATTRIBUTE,
      FilterableEntity.FIRMOGRAPHICS,
      FilterableEntity.LAST_TOUCHED
    ],
    ACTIVITIES: [
      FilterableEntity.ACCOUNT_OWNERS,
      FilterableEntity.TARGETED,
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL,
      FilterableEntity.CUSTOM_ATTRIBUTE,
      FilterableEntity.FIRMOGRAPHICS,
      FilterableEntity.LAST_TOUCHED
    ],
    ACCOUNT_DETAILS: [
      FilterableEntity.ACTIVITIES,
      FilterableEntity.CONTACT_TYPES,
      FilterableEntity.CONTACT_ROLE,
      FilterableEntity.PAGE_URL
    ],

    CONTACT_DETAILS: [FilterableEntity.ACTIVITIES, FilterableEntity.PAGE_URL]
  }

  return {
    ACCOUNTS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACCOUNTS.includes(filterType)
      )
    ],
    ACTIVITIES: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACTIVITIES.includes(filterType)
      )
    ],
    ACCOUNT_DETAILS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.ACCOUNT_DETAILS.includes(filterType)
      )
    ],
    CONTACT_DETAILS: [
      firstGroup.filter(({ filterType }: FilterValues) =>
        supportedFilters.CONTACT_DETAILS.includes(filterType)
      )
    ]
  }
}

export const formatFilterFromConfig = (filterConfiguration: FilterConfigAndAvailableValues) => {
  return {
    filterType: filterConfiguration.filterType,
    filterSubtype: filterConfiguration.filterSubtype,
    operator: filterConfiguration.operators[0],
    valueType: filterConfiguration.valueType,
    values: getDefaultFilterValues(
      filterConfiguration.filterType,
      filterConfiguration.operators[0],
      filterConfiguration.attributeType
    )
  }
}

export const getFilterConfig = (
  filter: { filterType: FilterableEntity; filterSubtype?: string },
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  return (
    filterConfiguration.find(
      fc => fc.filterType === filter.filterType && fc.filterSubtype === filter.filterSubtype
    ) || null
  )
}

export const getFirstUnusedFilter = (
  filterGroups: FilterGroup[],
  filterConfiguration: FilterConfigAndAvailableValues[]
) => {
  return filterConfiguration.find(
    config =>
      !filterGroups[filterGroups.length - 1]?.some(
        filter =>
          config.filterType === filter.filterType && config.filterSubtype === filter.filterSubtype
      )
  )
}

export const getActivityFiltersFromQueryString = (): FilterGroup[] | null => {
  try {
    const queryParams = new URLSearchParams(window.location.search.substr(1))
    if (queryParams.has('filters')) {
      const json = decodeURIComponent(atob(queryParams.get('filters') || ''))

      const filterGroupsByPage = deserializeFilters(json)
      if (filterGroupsByPage) {
        return filterGroupsByPage.ACTIVITIES
      }
    }
  } catch (e) {
    return null
  }

  return null
}

export const getDefaultFilterValues = (
  filterType: FilterableEntity,
  operator: FilterOperator,
  attributeType?: string
): any => {
  if (
    filterType === FilterableEntity.DATE_RANGE ||
    filterType === FilterableEntity.LAST_TOUCHED ||
    attributeType === 'DATE' ||
    attributeType === 'DATETIME'
  ) {
    return operator === FilterOperator.BETWEEN ? getDefaultRange() : getDefaultDate()
  }

  return null
}

export const updateFilterType = (
  nextFilter: FilterValues,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: any, j: number) =>
      i === groupIndex && j === filterIndex ? nextFilter : filter
    )
  )
}

export const updateFilterOperator = (
  operator: FilterOperator,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: FilterValues, j: number) => ({
      ...filter,
      operator: i === groupIndex && j === filterIndex ? operator : filter.operator,
      values:
        i === groupIndex && j === filterIndex
          ? getDefaultFilterValues(filter.filterType, operator, filter.attributeType) ||
          filter.values
          : filter.values
    }))
  )
}

export const updateFilterValue = (
  nextValues: any,
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups.map((group: any, i: number) =>
    group.map((filter: FilterValues, j: number) => ({
      ...filter,
      values: i === groupIndex && j === filterIndex ? nextValues : filter.values
    }))
  )
}

export const addFilter = (
  filter: FilterValues,
  filterGroups: FilterGroup[],
  groupIndex?: number
) => {
  const index = groupIndex ?? filterGroups.length - 1

  return filterGroups.map((group: FilterGroup, i: number) =>
    i === index ? [...group, filter] : group
  )
}

export const removeFilter = (
  filterGroups: FilterGroup[],
  groupIndex: number,
  filterIndex: number
) => {
  return filterGroups
    .map((group: FilterGroup, i: number) => {
      return group?.filter((_: any, j: number) => i !== groupIndex || j !== filterIndex)
    })
    ?.filter(group => group.length > 0)
}

export const addFilterGroup = (defaultFilter: FilterValues, filterGroups: FilterGroup[]) => {
  return [...filterGroups, [defaultFilter]]
}
